/**
 * SEO Helper Functions
 * Functions for generating structured data and SEO metadata
 */

import { CityData, StructuredDataSchema } from '../types/city.types';
import { getCityData, formatCityName } from '../config/cityData';
import { seoConfig } from '../config/seo.config';

// Ensure BASE_URL always has protocol
const getBaseUrl = (): string => {
  const url = seoConfig.baseUrl;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `https://${url}`;
};

const BASE_URL = getBaseUrl();
const BUSINESS_PHONE = seoConfig.phone;
const BUSINESS_EMAIL = seoConfig.email;
const GMB_URL = seoConfig.gmbUrl;

/**
 * Generate LocalBusiness schema for a city
 */
export const generateLocalBusinessSchema = (
  city: CityData,
  turfCount: number
): StructuredDataSchema => {
  const cityUrl = `${BASE_URL}/${city.slug}`;
  
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Cricket & Football Turfs in ${city.name}`,
    "description": `Explore verified sports turfs in ${city.name} with complete details, photos, and amenities.You can also book turf  at the exact venue price with no extra charges or hidden fees. ${turfCount}+ venues listed.`,
    "url": cityUrl,
    "logo": `${BASE_URL}/TBlogo.png`,
    "image": `${BASE_URL}/TBlogo.png`,
    "telephone": BUSINESS_PHONE,
    "email": BUSINESS_EMAIL,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Lalaram Nagar, Geetabhawan",
      "addressLocality": city.name,
      "addressRegion": city.state,
      "postalCode": "452001",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": city.latitude,
      "longitude": city.longitude
    },
    "areaServed": {
      "@type": "City",
      "name": city.name,
      "addressRegion": city.state,
      "addressCountry": "IN"
    },
    "serviceType": [
      "Sports Venue Information",
      "Cricket Turf Booking",
      "Football Ground Booking",
      "Box Cricket Booking"
    ],
    "priceRange": "₹₹",
    "openingHours": "Mo-Su 06:00-23:00",
    "sameAs": [
      GMB_URL, // Google Business Profile - links website to GMB
      seoConfig.socialLinks.instagram,
      seoConfig.socialLinks.linkedin
    ]
  };
};

/**
 * Generate Service schema for turf booking service
 */
export const generateServiceSchema = (city: CityData): StructuredDataSchema => {
  const cityUrl = `${BASE_URL}/${city.slug}`;
  
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Sports Venue Booking",
    "provider": {
      "@type": "LocalBusiness",
      "name": "TurfBooking.in",
      "url": BASE_URL,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Lalaram Nagar, Geetabhawan",
        "addressLocality": city.name,
        "addressRegion": city.state,
        "postalCode": "452001",
        "addressCountry": "IN"
      }
    },
    "areaServed": {
      "@type": "City",
      "name": city.name,
      "addressRegion": city.state,
      "addressCountry": "IN"
    },
    "description": `Explore sports turfs in ${city.name} with full details and photos, and book at the same venue price with no extra charges.`,
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": cityUrl,
      "serviceType": "Online"
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "INR",
      "url": cityUrl
    }
  };
};

/**
 * Generate Breadcrumb schema: Home › City
 */
export const generateBreadcrumbSchema = (city: CityData): StructuredDataSchema => {
  const baseUrl = BASE_URL.startsWith('http') ? BASE_URL : `https://${BASE_URL}`;
  const cityUrl = `${baseUrl}/${city.slug}`;
  
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": city.name,
        "item": cityUrl
      }
    ]
  };
};

/**
 * Generate all structured data schemas for a city (optionally area-scoped)
 */
export const generateAllSchemas = (
  citySlug: string,
  turfCount: number,
  areaName?: string | null,
  areaSlug?: string | null
): {
  localBusiness: StructuredDataSchema | null;
  service: StructuredDataSchema | null;
  breadcrumb: StructuredDataSchema | null;
} => {
  const city = getCityData(citySlug);
  
  if (!city) {
    return {
      localBusiness: null,
      service: null,
      breadcrumb: null
    };
  }

  if (areaName && areaSlug) {
    return {
      localBusiness: generateAreaLocalBusinessSchema(city, areaName, areaSlug, turfCount),
      service: generateAreaServiceSchema(city, areaName, areaSlug),
      breadcrumb: generateAreaBreadcrumbSchema(city, areaName, areaSlug)
    };
  }
  
  return {
    localBusiness: generateLocalBusinessSchema(city, turfCount),
    service: generateServiceSchema(city),
    breadcrumb: generateBreadcrumbSchema(city)
  };
};

/**
 * Generate LocalBusiness schema scoped to an area
 */
const generateAreaLocalBusinessSchema = (
  city: CityData,
  areaName: string,
  areaSlug: string,
  turfCount: number
): StructuredDataSchema => {
  const areaUrl = `${BASE_URL}/${city.slug}/area/${areaSlug}`;

  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Cricket & Football Turfs in ${areaName}, ${city.name}`,
    "description": `Explore verified sports turfs in ${areaName}, ${city.name} with complete details, photos, and amenities. Book at the exact venue price with no extra charges. ${turfCount}+ venues listed.`,
    "url": areaUrl,
    "logo": `${BASE_URL}/TBlogo.png`,
    "image": `${BASE_URL}/TBlogo.png`,
    "telephone": BUSINESS_PHONE,
    "email": BUSINESS_EMAIL,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": `${areaName}, ${city.name}`,
      "addressRegion": city.state,
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": city.latitude,
      "longitude": city.longitude
    },
    "areaServed": {
      "@type": "Place",
      "name": `${areaName}, ${city.name}`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": city.name,
        "addressRegion": city.state,
        "addressCountry": "IN"
      }
    },
    "serviceType": [
      "Sports Venue Information",
      "Cricket Turf Booking",
      "Football Ground Booking",
      "Box Cricket Booking"
    ],
    "priceRange": "₹₹",
    "openingHours": "Mo-Su 06:00-23:00",
    "sameAs": [
      GMB_URL,
      seoConfig.socialLinks.instagram,
      seoConfig.socialLinks.linkedin
    ]
  };
};

/**
 * Generate Service schema scoped to an area
 */
const generateAreaServiceSchema = (
  city: CityData,
  areaName: string,
  areaSlug: string
): StructuredDataSchema => {
  const areaUrl = `${BASE_URL}/${city.slug}/area/${areaSlug}`;

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Sports Venue Booking",
    "provider": {
      "@type": "LocalBusiness",
      "name": "TurfBooking.in",
      "url": BASE_URL,
    },
    "areaServed": {
      "@type": "Place",
      "name": `${areaName}, ${city.name}`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": city.name,
        "addressRegion": city.state,
        "addressCountry": "IN"
      }
    },
    "description": `Explore sports turfs in ${areaName}, ${city.name} with full details and photos, and book at the same venue price with no extra charges.`,
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": areaUrl,
      "serviceType": "Online"
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "INR",
      "url": areaUrl
    }
  };
};

/**
 * Generate Breadcrumb schema for area page: Home > City > Area
 */
const generateAreaBreadcrumbSchema = (
  city: CityData,
  areaName: string,
  areaSlug: string
): StructuredDataSchema => {
  const baseUrl = BASE_URL.startsWith('http') ? BASE_URL : `https://${BASE_URL}`;
  const cityUrl = `${baseUrl}/${city.slug}`;
  const areaUrl = `${baseUrl}/${city.slug}/area/${areaSlug}`;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": city.name,
        "item": cityUrl
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": areaName,
        "item": areaUrl
      }
    ]
  };
};

/**
 * Generate optimized page title for city (matching Playo's format)
 */
export const generateCityPageTitle = (cityName: string): string => {
  return `Book Turf in ${cityName} | Verified Cricket & Football Turfs | TurfBooking.in`;
};

/**
 * Generate optimized meta description for city (matching Playo's snippet format)
 */
export const generateCityMetaDescription = (
  cityName: string,
  turfCount: number
): string => {
  return `Book turf in ${cityName} with TurfBooking.in. Explore ${turfCount}+ verified cricket and football turfs, compare prices, photos, amenities and book at venue price with no extra charges.`;
};

/**
 * Generate keywords string for city
 */
export const generateCityKeywords = (citySlug: string): string => {
  const city = getCityData(citySlug);
  if (!city) return '';
  
  return city.keywords.join(', ');
};

/**
 * Get canonical URL for city (without trailing slash)
 */
export const getCityCanonicalUrl = (citySlug: string): string => {
  return `${BASE_URL}/${citySlug}`;
};

/**
 * Generate page title for area page
 */
export const generateAreaPageTitle = (areaName: string, cityName: string): string => {
  return `Book Turf in ${areaName}, ${cityName} | Verified Grounds Near You | TurfBooking.in`;
};

/**
 * Generate meta description for area page
 */
export const generateAreaMetaDescription = (
  areaName: string,
  cityName: string,
  turfCount: number
): string => {
  return `Compare ${turfCount}+ verified turfs in ${areaName}, ${cityName}. Check prices, photos, amenities, timings and book cricket or football turfs online at venue price on TurfBooking.in.`;
};

/**
 * Generate keywords for area page
 */
export const generateAreaKeywords = (areaName: string, citySlug: string): string => {
  const city = getCityData(citySlug);
  const cityName = city?.name || formatCityName(citySlug);
  const areaLower = areaName.toLowerCase();
  const cityLower = cityName.toLowerCase();

  return [
    `turf booking ${areaLower} ${cityLower}`,
    `cricket turf ${areaLower}`,
    `football turf ${areaLower}`,
    `box cricket ${areaLower}`,
    `sports ground ${areaLower} ${cityLower}`,
    `turf near me ${areaLower}`,
    `book turf ${areaLower} ${cityLower}`,
    ...(city?.keywords || []),
  ].join(', ');
};

/**
 * Get canonical URL for area page (without trailing slash)
 */
export const getAreaCanonicalUrl = (citySlug: string, areaSlug: string): string => {
  return `${BASE_URL}/${citySlug}/area/${areaSlug}`;
};

/**
 * Get dynamic canonical URL for any page path
 * Ensures consistent format: baseUrl + path (no trailing slash)
 */
export const getCanonicalUrl = (path: string = '/'): string => {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const finalPath = cleanPath === '' ? '' : `/${cleanPath}`;
  return `${BASE_URL}${finalPath}`;
};

const slugifyLocation = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export const generateListingItemListSchema = ({
  citySlug,
  areaName,
  turfNames,
}: {
  citySlug: string;
  areaName?: string | null;
  turfNames: string[];
}): StructuredDataSchema | null => {
  if (!turfNames.length) return null;

  const areaSlug = areaName ? slugifyLocation(areaName) : null;
  const listingUrl = areaSlug
    ? `${BASE_URL}/${citySlug}/area/${areaSlug}`
    : `${BASE_URL}/${citySlug}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'name': areaName
      ? `Top turfs in ${areaName}, ${formatCityName(citySlug)}`
      : `Top turfs in ${formatCityName(citySlug)}`,
    'url': listingUrl,
    'numberOfItems': turfNames.length,
    'itemListElement': turfNames.map((name, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': name,
    })),
  };
};

export const generateListingFaqSchema = ({
  citySlug,
  areaName,
  turfCount,
}: {
  citySlug: string;
  areaName?: string | null;
  turfCount: number;
}): StructuredDataSchema => {
  const cityName = formatCityName(citySlug);
  const location = areaName ? `${areaName}, ${cityName}` : cityName;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': `How can I book a turf in ${location}?`,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': `Browse verified turfs in ${location} on TurfBooking.in, compare photos, prices, timings and amenities, then choose the turf that fits your game and book at venue price.`,
        },
      },
      {
        '@type': 'Question',
        'name': `How many turfs are listed in ${location}?`,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': `TurfBooking.in currently lists ${turfCount}+ verified turf options in ${location}, including cricket and football venues depending on availability.`,
        },
      },
      {
        '@type': 'Question',
        'name': `What details can I check before booking a turf in ${location}?`,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': `You can check pricing, photos, amenities, exact location, area, playing dimensions, contact details and available time slots before choosing a turf in ${location}.`,
        },
      },
    ],
  };
};

// ─── Turf Detail Page SEO Helpers ────────────────────────────────────────────

export interface TurfSEOInput {
  turfName: string;
  username: string;
  pitchName?: string;
  citySlug: string;
  areaName?: string | null;
  address?: string;
  price?: number;
  amenities?: string[];
  imageUrl?: string;
  latitude?: number | string;
  longitude?: number | string;
  phone?: string;
}

export const generateTurfPageTitle = (input: TurfSEOInput): string => {
  const cityName = formatCityName(input.citySlug);
  const location = input.areaName
    ? `${input.areaName}, ${cityName}`
    : cityName;

  return `${input.turfName} in ${location} | Book Online | TurfBooking.in`;
};

export const generateTurfMetaDescription = (input: TurfSEOInput): string => {
  const cityName = formatCityName(input.citySlug);
  const location = input.areaName
    ? `${input.areaName}, ${cityName}`
    : cityName;

  const parts = [`Book ${input.turfName} in ${location}.`];

  if (input.address) {
    parts.push(`Located at ${input.address}.`);
  }
  if (input.price) {
    parts.push(`Starting at ₹${input.price}/hr.`);
  }

  parts.push('Check photos, pricing, facilities & book at venue price with no extra charges on TurfBooking.in.');

  return parts.join(' ');
};

export const generateTurfKeywords = (input: TurfSEOInput): string => {
  const cityName = formatCityName(input.citySlug);
  const cityLower = cityName.toLowerCase();
  const turfLower = input.turfName.toLowerCase();
  const areaLower = input.areaName?.toLowerCase();

  const keywords = [
    input.turfName,
    `${turfLower} ${cityLower}`,
    `${turfLower} booking`,
    `book ${turfLower}`,
    `${turfLower} price`,
    `cricket turf ${cityLower}`,
    `football turf ${cityLower}`,
    `turf booking ${cityLower}`,
  ];

  if (areaLower) {
    keywords.push(
      `${turfLower} ${areaLower}`,
      `${turfLower} ${areaLower} ${cityLower}`,
      `turf in ${areaLower}`,
      `cricket turf ${areaLower} ${cityLower}`,
      `football turf ${areaLower} ${cityLower}`,
      `turf booking ${areaLower}`,
    );
  }

  return keywords.join(', ');
};

export const getTurfCanonicalUrl = (input: TurfSEOInput): string => {
  const areaSlug = input.areaName
    ? input.areaName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    : null;

  const pitchSegment = input.pitchName || `${input.username}-p1-0`;

  if (areaSlug) {
    return `${BASE_URL}/${input.citySlug}/area/${areaSlug}/${input.username}/${pitchSegment}`;
  }
  return `${BASE_URL}/${input.citySlug}/${input.username}/${pitchSegment}`;
};

export const generateTurfStructuredData = (input: TurfSEOInput): StructuredDataSchema => {
  const canonicalUrl = getTurfCanonicalUrl(input);
  const cityName = formatCityName(input.citySlug);
  const cityData = getCityData(input.citySlug);
  const location = input.areaName
    ? `${input.areaName}, ${cityName}`
    : cityName;

  const schema: StructuredDataSchema = {
    '@context': 'https://schema.org',
    '@type': 'SportsActivityLocation',
    'name': input.turfName,
    'description': `${input.turfName} - Sports turf in ${location}. ${input.address || ''}`.trim(),
    'url': canonicalUrl,
    'image': input.imageUrl || `${BASE_URL}/TBlogo.png`,
    'telephone': input.phone || BUSINESS_PHONE,
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': input.address || '',
      'addressLocality': input.areaName ? `${input.areaName}, ${cityName}` : cityName,
      'addressRegion': cityData?.state || 'India',
      'addressCountry': 'IN',
    },
    'sport': ['Cricket', 'Football', 'Box Cricket'],
    'isAccessibleForFree': false,
    'openingHours': 'Mo-Su 06:00-23:00',
    'currenciesAccepted': 'INR',
  };

  if (input.latitude && input.longitude) {
    schema['geo'] = {
      '@type': 'GeoCoordinates',
      'latitude': Number(input.latitude),
      'longitude': Number(input.longitude),
    };
  }

  if (input.price) {
    schema['offers'] = {
      '@type': 'Offer',
      'priceCurrency': 'INR',
      'price': input.price,
      'availability': 'https://schema.org/InStock',
      'url': canonicalUrl,
    };
  }

  if (input.amenities && input.amenities.length > 0) {
    schema['amenityFeature'] = input.amenities.map((a) => ({
      '@type': 'LocationFeatureSpecification',
      'name': a,
    }));
  }

  return schema;
};

export const generateTurfBreadcrumbSchema = (input: TurfSEOInput): StructuredDataSchema => {
  const cityName = formatCityName(input.citySlug);
  const areaSlug = input.areaName
    ? input.areaName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    : null;

  const items: Array<{ name: string; item: string }> = [
    { name: 'Home', item: BASE_URL },
    { name: cityName, item: `${BASE_URL}/${input.citySlug}` },
  ];

  if (input.areaName && areaSlug) {
    items.push({
      name: input.areaName,
      item: `${BASE_URL}/${input.citySlug}/area/${areaSlug}`,
    });
  }

  items.push({ name: input.turfName, item: getTurfCanonicalUrl(input) });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': items.map((entry, i) => ({
      '@type': 'ListItem',
      'position': i + 1,
      'name': entry.name,
      'item': entry.item,
    })),
  };
};

/**
 * Format city name (capitalize first letter)
 * Re-export from cityData for convenience
 */
export { formatCityName };
